import { useEffect, useRef } from 'react';

type CallbackFn = (...args: unknown[]) => Promise<void>;

interface UseIntervalArgs {
  callback: CallbackFn;
  delay: number;
  pauseWhenBackgrounded?: boolean;
}

/**
 * useInterval
 *
 * React hook wrapper for setInterval. Taken from:
 * https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 */
const useInterval = ({ callback, delay, pauseWhenBackgrounded = false }: UseIntervalArgs) => {
  const savedCallback = useRef<CallbackFn>();
  const isWindowBackgrounded = useRef<boolean>(false);

  useEffect(() => {
    function handleWindowVisibilityChange() {
      if (document.hidden) {
        isWindowBackgrounded.current = true;
      } else {
        isWindowBackgrounded.current = false;
      }
    }
    document.addEventListener('visibilitychange', handleWindowVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleWindowVisibilityChange);
  }, []);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (pauseWhenBackgrounded && isWindowBackgrounded.current) {
        return;
      }
      savedCallback.current?.();
    }
    if (delay != null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

export default useInterval;
